import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretUpIcon } from "@phosphor-icons/react/dist/csr/CaretUp";
import { type ReactNode, useMemo, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { EmptyTableMessage } from "@volca/ui/components/ui/empty-table-message";
import { Frame } from "@volca/ui/components/ui/frame";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@volca/ui/components/ui/table";
import { cn } from "@volca/ui/lib/utils";
import { MissingFilter } from "./missing-filter";
import { compareValues } from "./table-values";

export interface TableColumn<Row> {
  id: string;
  header: string;
  icon?: ReactNode;
  cell: (row: Row) => ReactNode;
  /** What the column sorts by; unsortable without it. */
  sortValue?: (row: Row) => string | number | null;
  align?: "start" | "end";
  /**
   * The total under the column, when it has one: given the rows shown, so a
   * table filtered to some rows totals those.
   */
  total?: ReactNode | ((rows: Row[]) => ReactNode);
  /**
   * Whether a row has no figure in this column (FIN-004). The footer then
   * says how many ("3 missing"), and that note filters the table to them.
   */
  missing?: (row: Row) => boolean;
  className?: string;
}

const PAGE = 200;

export const ObjectTable = <Row,>({
  rows,
  columns,
  rowKey,
  rowLabel,
  selectedKey,
  onOpenRow,
  countLabel,
  label,
  defaultSort,
  fill = false,
  className,
}: {
  rows: Row[];
  columns: TableColumn<Row>[];
  rowKey: (row: Row) => string;
  rowLabel?: (row: Row) => string;
  selectedKey?: string | null;
  /** Also given the rows in the order shown, to step through them. */
  onOpenRow?: (row: Row, rows: Row[]) => void;
  countLabel: string;
  label: string;
  defaultSort?: { column: string; direction: "asc" | "desc" };
  /**
   * Fit the parent's height: as tall as its rows, at most the height the
   * parent gives it, scrolling inside past that with the header and totals
   * held in place, so a long list never scrolls the page and a short one
   * doesn't stretch. The parent is a flex column.
   */
  fill?: boolean;
  className?: string;
}) => {
  const [sort, setSort] = useState(defaultSort);
  const [shown, setShown] = useState(PAGE);
  const [filter, setFilter] = useState<string | null>(null);
  const missingCounts = useMemo(
    () =>
      Object.fromEntries(
        columns.map((column) => [
          column.id,
          column.missing ? rows.filter(column.missing).length : 0,
        ]),
      ),
    [rows, columns],
  );
  // A filter on a column with nothing missing (the rows changed) is no filter.
  const narrowedBy = columns.find(
    (column) => column.id === filter && missingCounts[column.id] > 0,
  );
  const visible = useMemo(
    () => (narrowedBy?.missing ? rows.filter(narrowedBy.missing) : rows),
    [rows, narrowedBy],
  );
  const sorted = useMemo(() => {
    const column = columns.find((item) => item.id === sort?.column);
    if (!column?.sortValue || !sort) return visible;
    const value = column.sortValue;
    // Blanks, and missing figures, stay last whichever way it is sorted.
    return [...visible].sort((a, b) =>
      compareValues(value(a), value(b), sort.direction),
    );
  }, [visible, columns, sort]);
  const toggle = (id: string) =>
    setSort((current) =>
      current?.column === id
        ? {
            column: id,
            direction: current.direction === "asc" ? "desc" : "asc",
          }
        : { column: id, direction: "desc" },
    );
  const align = (column: TableColumn<Row>) =>
    cn({ "text-right tabular-nums": column.align === "end" }, column.className);

  if (!rows.length)
    return (
      <EmptyTableMessage>{`No ${label.toLowerCase()} yet.`}</EmptyTableMessage>
    );

  return (
    <Frame
      spacing="xs"
      // With `fill`, it is as tall as its rows and never taller than the
      // space below the tabs: it scrolls inside past that, and a short list
      // doesn't stretch to the bottom of the page.
      className={cn("w-full", { "min-h-0 shrink": fill }, className)}
    >
      <Table variant="card" scroll={fill} aria-label={label}>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.id}
                scope="col"
                aria-sort={
                  sort?.column === column.id
                    ? sort.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : undefined
                }
                className={align(column)}
              >
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 [&_svg]:size-3.5",
                    {
                      "flex-row-reverse": column.align === "end",
                    },
                  )}
                >
                  {column.icon}
                  {column.sortValue ? (
                    <button
                      type="button"
                      onClick={() => toggle(column.id)}
                      className="inline-flex items-center gap-1 hover:text-ink"
                    >
                      {column.header}
                      {sort?.column === column.id &&
                        (sort.direction === "asc" ? (
                          <CaretUpIcon aria-hidden />
                        ) : (
                          <CaretDownIcon aria-hidden />
                        ))}
                    </button>
                  ) : (
                    column.header
                  )}
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.slice(0, shown).map((row) => {
            const key = rowKey(row);
            return (
              <TableRow
                key={key}
                data-state={selectedKey === key ? "selected" : undefined}
                onClick={onOpenRow ? () => onOpenRow(row, sorted) : undefined}
                // `group/source`: a "Source" button in the row shows on hover.
                className={cn("group/source", {
                  "relative cursor-pointer": !!onOpenRow,
                })}
              >
                {columns.map((column, index) => (
                  <TableCell
                    key={column.id}
                    className={cn("max-w-80 truncate", align(column))}
                  >
                    {/* The row opens from the keyboard through a button over
                    it. The pointer passes through to the row, so a link to
                    another record in a cell still takes its own click. */}
                    {index === 0 && onOpenRow && (
                      <button
                        type="button"
                        aria-label={`Open ${rowLabel?.(row) ?? "record"}`}
                        aria-current={selectedKey === key ? "true" : undefined}
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenRow(row, sorted);
                        }}
                        className="pointer-events-none absolute inset-0 rounded-lg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink"
                      />
                    )}
                    {column.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell key={column.id} className={align(column)}>
                {index === 0 ? (
                  narrowedBy ? (
                    <span className="inline-flex items-center gap-2">
                      <span>{`${visible.length} of ${countLabel}`}</span>
                      <Button
                        variant="link"
                        size="xs"
                        className="h-auto p-0"
                        onClick={() => setFilter(null)}
                      >
                        Show all
                      </Button>
                    </span>
                  ) : (
                    countLabel
                  )
                ) : (
                  <>
                    {typeof column.total === "function"
                      ? column.total(visible)
                      : column.total}
                    <MissingFilter
                      count={missingCounts[column.id]}
                      header={column.header}
                      active={narrowedBy?.id === column.id}
                      align={column.align}
                      onToggle={() =>
                        setFilter(
                          narrowedBy?.id === column.id ? null : column.id,
                        )
                      }
                    />
                  </>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      </Table>
      {sorted.length > shown && (
        <Button
          variant="ghost"
          size="sm"
          className="self-center"
          onClick={() => setShown(shown + PAGE)}
        >
          Show {Math.min(PAGE, sorted.length - shown)} more
        </Button>
      )}
    </Frame>
  );
};
